import PicFloorplansConfig, { PicFloorPlansOptionId } from '~/tenants/pic/performable/floorplans/PicFloorplansConfig';
import PicJob from '~/tenants/pic/model/PicJob';
import { TenantOrderLine } from '~/lib/model';
import { Big } from 'big.js';
import { schema_latest_version, ZodVersionedMetadata } from '~/lib/zod';
import { PicOrderType } from '~/tenants/pic/model/PicOrder';
import Pic3dConfig from '~/tenants/pic/performable/3d/Pic3dConfig';

export interface PicFloorPlansOptions {
  onsite: number; // TODO: need official numbers
  revenue: number;
  from: number;
  to: number;
}

export const Pic_FLOORPLANS_OPTIONS: Record<PicFloorPlansOptionId, PicFloorPlansOptions> = {
  [PicFloorPlansOptionId.ONE]: {
    onsite: 10,
    revenue: 60,
    from: 0,
    to: 1000,
  },
  [PicFloorPlansOptionId.TWO]: {
    onsite: 10,
    revenue: 80,
    from: 1001,
    to: 2000,
  },
  [PicFloorPlansOptionId.THREE]: {
    onsite: 15,
    revenue: 100,
    from: 2001,
    to: 3000,
  },
  [PicFloorPlansOptionId.FOUR]: {
    onsite: 15,
    revenue: 120,
    from: 3001,
    to: 4000,
  },
  [PicFloorPlansOptionId.FIVE]: {
    onsite: 20,
    revenue: 140,
    from: 4001,
    to: 5000,
  },
  [PicFloorPlansOptionId.SIX]: {
    onsite: 20,
    revenue: 160,
    from: 5001,
    to: 6000,
  },
  [PicFloorPlansOptionId.SEVEN]: {
    onsite: 30,
    revenue: 180,
    from: 6001,
    to: 7000,
  },
  [PicFloorPlansOptionId.EIGHT]: {
    onsite: 45,
    revenue: 200,
    from: 7001,
    to: 8000,
  },
};

export default class PicFloorplansJob extends PicJob<typeof PicFloorplansConfig> {
  get performable() {
    return PicFloorplansConfig;
  }

  onsite(): number {
    return Pic_FLOORPLANS_OPTIONS[this.metadata.option].onsite;
  }

  defaultValue(): ZodVersionedMetadata<(typeof PicFloorplansConfig)['schema']> {
    let option: undefined | PicFloorPlansOptionId = undefined;

    if (this.order.metadata.type === PicOrderType.DRAFT) {
      return {
        option: PicFloorPlansOptionId.ONE,
        version: schema_latest_version(Pic3dConfig.schema),
      };
    }

    for (const [key, value] of Object.entries(Pic_FLOORPLANS_OPTIONS)) {
      if (this.order.metadata.sqft >= value.from && this.order.metadata.sqft <= value.to) {
        // TODO: better typing
        option = key as any as PicFloorPlansOptionId;
        break;
      }
    }

    if (!option) {
      throw new Error('No option found for sqft range');
    }

    return {
      option,
      version: schema_latest_version(PicFloorplansConfig.schema),
    };
  }

  get configurable() {
    return false;
  }

  get multiple() {
    return this.order.metadata.type !== PicOrderType.COMMERCIAL;
  }

  get hourly() {
    return this.order.metadata.type === PicOrderType.COMMERCIAL;
  }

  revenueLines(): TenantOrderLine[] {
    if (this.order.metadata.type === PicOrderType.COMMERCIAL) {
      return [];
    }

    const option = Pic_FLOORPLANS_OPTIONS[this.metadata.option];

    const amount = new Big(option.revenue);

    return [
      {
        amount,
        description: this.performable.name,
        id: this.performable.id,
        discountable: true,
      },
    ];
  }
}
